import { Flex, Link } from '@lokalise/louis';
import { useResizeDetector } from 'react-resize-detector';

import type { RootState } from '@app/types';

import BranchSelect from './components/BranchSelect';
import ExploreDesignPlugins from './components/ExploreDesignPlugins';
import ProjectNavigation from './components/ProjectNavigation';
import { Breadcrumbs, BreadcrumbsContainer, Container, HeaderContent } from './styles';

type ProjectHeaderProps = {
    project: RootState['project'];
    branch: RootState['branch'];
    userRights: RootState['userRights'];
    activeItem: string;
    sticky?: boolean;
};

const DEFAULT_HEADER_HEIGHT = 77;

const ProjectHeader = ({ project, branch, userRights, activeItem, sticky }: ProjectHeaderProps) => {
    // need to detect when headerRef changes height to avoid sticky header glitch
    const { ref: headerRef, height } = useResizeDetector({ refreshMode: 'debounce', refreshRate: 0 });

    const staticEnabled = () => {
        if (window.pageYOffset === 0) {
            return true;
        }

        return !sticky;
    };

    return (
        <Container $headerHeight={height || DEFAULT_HEADER_HEIGHT}>
            <div>
                <HeaderContent ref={headerRef} $staticEnabled={staticEnabled()}>
                    <BreadcrumbsContainer>
                        <Breadcrumbs aria-label="breadcrumb" role="navigation">
                            <Link href="/projects" rel="noreferrer">
                                Projects
                            </Link>
                            {' — '}
                            <Link href={`/project/${project.id}/`} rel="noreferrer" aria-current="page">
                                {project.name}
                            </Link>
                        </Breadcrumbs>
                        {project.isBranchingEnabled && (
                            <BranchSelect
                                projectId={project.id}
                                currentBranchName={branch.branchName}
                                hasAdminRightsSettings={userRights.adminRights.settings}
                                hasAdminRightsCreateBranch={userRights.adminRights.createBranches}
                                branches={branch}
                                view={project.view}
                                search={project.search}
                            />
                        )}
                    </BreadcrumbsContainer>
                    <Flex gap={2} justify="space-between" wrap="nowrap">
                        <ProjectNavigation activeItem={activeItem} />
                        <ExploreDesignPlugins />
                    </Flex>
                </HeaderContent>
            </div>
        </Container>
    );
};

export default ProjectHeader;
